<template>
  <component
    :is="currentCountryForm"
    v-bind="props"
    v-model:city="city"
    v-model:company="company"
    v-model:country="country"
    v-model:first-name="firstName"
    v-model:first-name-pronunciation="firstNamePronunciation"
    v-model:last-name="lastName"
    v-model:last-name-pronunciation="lastNamePronunciation"
    v-model:phone="phone"
    v-model:postal-code="postalCode"
    v-model:public-company-name="publicCompanyName"
    v-model:state-or-province="stateOrProvince"
    v-model:street="street"
    v-model:street2="street2"
    :data-test="`${currentCountry}-address-fields`"
  ></component>
</template>

<script setup lang="ts">
import { type Component, computed, watch } from 'vue'

import { Country } from '@backmarket/http-api'

import type { Address } from '../../types/address'
import type { AddressFieldsProps } from '../../types/addressFields'

import AUAddressFields from './LocalizedAddressFields/AUAddressFields.vue'
import DefaultAddressFields from './LocalizedAddressFields/DefaultAddressFields.vue'
import JPAddressFields from './LocalizedAddressFields/JPAddressFields.vue'
import USAddressFields from './LocalizedAddressFields/USAddressFields.vue'

const props = withDefaults(defineProps<AddressFieldsProps>(), {
  defaultCountry: Country.US,
})

const firstName = defineModel<NonNullable<Address['firstName']>>('firstName')
const lastName = defineModel<NonNullable<Address['lastName']>>('lastName')
const firstNamePronunciation = defineModel<
  NonNullable<Address['firstNamePronunciation']>
>('firstNamePronunciation')
const lastNamePronunciation = defineModel<
  NonNullable<Address['lastNamePronunciation']>
>('lastNamePronunciation')
const company = defineModel<NonNullable<Address['company']>>('company')
const publicCompanyName = defineModel<string>('publicCompanyName')
const country = defineModel<NonNullable<Address['country']>>('country')
const stateOrProvince =
  defineModel<NonNullable<Address['stateOrProvince']>>('stateOrProvince')
const street = defineModel<NonNullable<Address['street']>>('street')
const street2 = defineModel<NonNullable<Address['street2']>>('street2')
const postalCode = defineModel<NonNullable<Address['postalCode']>>('postalCode')
const city = defineModel<NonNullable<Address['city']>>('city')
const phone = defineModel<NonNullable<Address['phone']>>('phone')

const addressFieldsComponents: Partial<Record<Country, Component>> = {
  [Country.AU]: AUAddressFields,
  [Country.JP]: JPAddressFields,
  [Country.US]: USAddressFields,
}

const currentCountry = computed<Country>(() =>
  Object.values(Country).some((_country) => _country === country.value)
    ? (country.value as Country)
    : (props.defaultCountry as Country),
)

const currentCountryForm = computed(
  () => addressFieldsComponents[currentCountry.value] ?? DefaultAddressFields,
)

watch(currentCountry, (newCountry) => {
  const countriesWithStates = [Country.US, Country.AU, Country.JP]

  if (stateOrProvince.value && !countriesWithStates.includes(newCountry)) {
    stateOrProvince.value = ''
  }

  if (newCountry !== Country.JP) {
    if (firstNamePronunciation.value) firstNamePronunciation.value = ''
    if (lastNamePronunciation.value) lastNamePronunciation.value = ''
  }
})
</script>
